import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectToken } from '@store/slices/authSlice';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { paymentApi } from '@services/paymentApi';

const PaymentCapture = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useSelector(selectToken);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    const capturePayment = async () => {
      try {
        setLoading(true);
        setError(null);

        const orderIdParam = searchParams.get('orderId') || searchParams.get('token');
        const planParam = searchParams.get('plan');
        const amountParam = searchParams.get('amount');
        const tokenFromQuery = searchParams.get('token');

        if (!orderIdParam) throw new Error('Order ID not found in URL parameters');

        setOrderId(orderIdParam);

        console.log('🔄 Capturing payment for order:', orderIdParam);
        console.log('📋 Plan:', planParam);
        console.log('💰 Amount:', amountParam);

        const result = await paymentApi.captureOrder(orderIdParam, token || tokenFromQuery);

        console.log('✅ Payment capture result:', result);

        if (result.success) {
          setPaymentStatus({
            success: true,
            message: result.message || 'Payment captured successfully!',
            plan: result.plan,
            amount: result.amount
          });
        } else {
          throw new Error(result.message || 'Payment capture failed');
        }
      } catch (err) {
        console.error('❌ Payment capture error:', err);
        setError(err.message || 'Failed to capture payment');
      } finally {
        setLoading(false);
      }
    };

    const tokenFromQuery = searchParams.get('token');
    if (token || tokenFromQuery) {
      capturePayment();
    } else {
      setError('Authentication token not found. Please login first.');
      setLoading(false);
    }
  }, [searchParams, token]);

  const handleRetry = () => window.location.reload();
  const handleGoToMembership = () => navigate('/membership');

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
        >
          <CircularProgress size={60} />
        </motion.div>
        <Typography variant="h6" color="text.secondary">
          Processing your payment...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we confirm your payment
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 2
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            {error ? (
              <>
                <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom color="error">
                  Payment Failed
                </Typography>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Order ID: {orderId}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button variant="outlined" onClick={handleRetry}>
                    Try Again
                  </Button>
                  <Button variant="contained" onClick={handleGoToMembership}>
                    Back to Plans
                  </Button>
                </Box>
              </>
            ) : paymentStatus?.success ? (
              <>
                <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom color="success.main">
                  Payment Successful!
                </Typography>
                <Alert severity="success" sx={{ mb: 3 }}>
                  {paymentStatus.message}
                </Alert>
                <Box sx={{ mb: 3 }}>
                  {paymentStatus.plan && (
                    <Chip
                      label={`Plan: ${paymentStatus.plan}`}
                      color="primary"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  )}
                  {paymentStatus.amount && (
                    <Chip
                      label={`Amount: $${paymentStatus.amount}`}
                      color="secondary"
                      sx={{ mb: 1 }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Your subscription has been activated. You can now enjoy all features!
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button 
                    variant="contained" 
                    href="https://wa.me/+14128767788"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      backgroundColor: '#25D366',
                      '&:hover': { backgroundColor: '#128C7E' }
                    }}
                  >
                    Start Using Bot
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <PaymentIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Processing Payment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we process your payment...
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default PaymentCapture;
